import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { AttrPath, CategorieUitstroomniveauVSO, Table, TussentijdseUitstroomVSO, UitstroomtypeVSO } from '@cumlaude/metadata';
import { generateCssClassForString } from '@cumlaude/shared-utils';
import { camelCase, groupBy, last, range } from 'lodash-es';
import { Observable } from 'rxjs';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { countRecords, SingleAggregator } from '../../services/aggregation';
import { Level, Path } from '../../services/data-tree';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	defaultDoorstroomAvailableGroups,
	ExportFilter,
} from '../../services/data.service';
import { att, percOfRow } from '../../services/measures';
import { BarInfo } from '../../services/stacked-bars';
import { DashboardVariant } from '../../services/weergave-opties';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { Attributes, getLevelFilters } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

export interface TussentijdseUitstroomI extends Attributes {
	ds_nm_tussentijdse_uitstroom_vso: TussentijdseUitstroomVSO;
}

export interface TussentijdseUitstroomA extends Attributes {
	regulierOnderwijs: number;
	speciaalOnderwijs: number;
	onbekend: number;
	bekend: number;
}

@Component({
	selector: 'app-tussentijdse-uitstroom',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './tussentijdse-uitstroom.component.html',
	styleUrls: [],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		FilterBarComponent,
		VbarchartTableComponent,
		WeergaveOptieComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class TussentijdseUitstroomComponent extends BarchartTableConfig<TussentijdseUitstroomI, TussentijdseUitstroomA> {
	private readonly dataService = inject(DataService);

	override availableGroups: AttrPath[] = [
		...defaultDoorstroomAvailableGroups,
		['ds_fk_lb_van', 'lb_nm_uitstroomniveau_vso'],
		['ds_fk_lb_van', 'lb_nm_beoogd_uitstroomniveau_vso'],
		['ds_fk_ll', 'll_des_uitschrijving'],
	];

	historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));
	historieSubgroups = computed(() => {
		return [...this.groeperingService.huidigeGroeperingen().slice(-1), ['ds_nm_schooljaar_van'] as AttrPath, ...this.subgroups];
	});

	readonly subgroups: AttrPath[] = [['ds_nm_tussentijdse_uitstroom_vso']];

	readonly actueelFilters: FilterName[] = [
		'x_prestatieanalyse_ds_schooljaar',
		'ds_fk_br_vest_van.br_co_brin',
		'ds_fk_vs_van.vs_nm_vestiging',
		'ds_fk_ilt_van.ilt_nm_niveau',
		'ds_nr_leerjaar_van',
		'ds_nm_prestatieanalyse_vso_uitzondering',
		'ds_fk_ll.ll_des_uitschrijving',
	];

	readonly historieFilters: FilterName[] = [
		'x_doorstroom_schooljaar_historie', //
		'x_doorstroom_multiselect_schooljaar',
		...this.actueelFilters.slice(1),
	];

	readonly permanentFilterExpressions = [
		// Bij VSO speelt 1 oktober geen rol, leerlingen stromen het hele jaar in en uit. We laten dus gegevens zien van de plaatsing waarin de leerling daadwerkelijk uitstroomt.
		new BasicFilterExpression(['ds_is_plaatsing_opeenvolgend'], 1),
		new BasicFilterExpression(['ds_nm_uitstroomtype_vso'], UitstroomtypeVSO.TUSSENTIJDSE_UITSTROOM),
		new BasicFilterExpression(['ds_fk_vs_van', 'vs_is_vso'], 1),
		// De leerlingen die tussentijds uitstromen naar arbeid (obv uitstroomniveau) worden niet meegenomen in deze berekening.
		new BasicFilterExpression(['ds_fun_nm_categorie_uitstroomniveau_vso'], [CategorieUitstroomniveauVSO.VERVOLGONDERWIJS, null], 'in'),
	];

	readonly factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData({
			...options,
			r: range(0, this.groeperingService.huidigeAantalGroeperingen() + 1),
		});
	}

	variant = this.qp.signal('variant');

	tussentijdseUitstroomVso = this.qp.signal('tussentijdseuitstroomvso');

	constructor() {
		super();
		effect(() => {
			this.tussentijdseUitstroomVso();
			this.filterService.refresh();
		});
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	protected override singleAggregators: Partial<{
		[ai in keyof TussentijdseUitstroomA]: SingleAggregator<TussentijdseUitstroomI, TussentijdseUitstroomA[ai]>;
	}> = {
		regulierOnderwijs: countRecords(
			(attrs: TussentijdseUitstroomI) => attrs.ds_nm_tussentijdse_uitstroom_vso === TussentijdseUitstroomVSO.REGULIER_ONDERWIJS
		),

		speciaalOnderwijs: countRecords(
			(attrs: TussentijdseUitstroomI) => attrs.ds_nm_tussentijdse_uitstroom_vso === TussentijdseUitstroomVSO.SPECIAAL_ONDERWIJS
		),
		onbekend: countRecords((attrs: TussentijdseUitstroomI) => attrs.ds_nm_tussentijdse_uitstroom_vso === null),
		bekend: countRecords((attrs: TussentijdseUitstroomI) => attrs.ds_nm_tussentijdse_uitstroom_vso !== null),
	};

	override partitionBarData(
		rowRoot: Level<TussentijdseUitstroomA, number[]>,
		_context: DashboardContext<TussentijdseUitstroomI, TussentijdseUitstroomA, TussentijdseUitstroomComponent>
	): Path<TussentijdseUitstroomA, number[]>[][] {
		const { regulierOnderwijs, speciaalOnderwijs, onbekend } = groupBy(rowRoot.r, (r) => camelCase(last(r)?.k ?? 'Onbekend'));
		return [regulierOnderwijs, speciaalOnderwijs, onbekend].filter((records) => records?.length);
	}

	override makeBar(
		attrs: TussentijdseUitstroomI,
		path: Path<TussentijdseUitstroomA, number[]>,
		context: DashboardContext<TussentijdseUitstroomI, TussentijdseUitstroomA, TussentijdseUitstroomComponent>
	): BarInfo {
		const uitstroom = this.displayService.display(attrs.ds_nm_tussentijdse_uitstroom_vso, ['ds_nm_tussentijdse_uitstroom_vso']);
		return {
			...super.makeBar(attrs, path, context),
			text: this.variant() === DashboardVariant.ACTUEEL ? uitstroom : undefined,
			tooltip: `${uitstroom}: ${attrs.count_records}`,
			className: generateCssClassForString(uitstroom),
		};
	}

	override createMeasureColumns(
		context: DashboardContext<TussentijdseUitstroomI, TussentijdseUitstroomA, TussentijdseUitstroomComponent>
	): ColumnDef<DataRow<TussentijdseUitstroomA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		const columns = [];
		switch (this.tussentijdseUitstroomVso()) {
			case TussentijdseUitstroomVSO.REGULIER_ONDERWIJS:
				columns.push(
					this.createLeerlingColumn(
						'% regulier',
						percOfRow('regulierOnderwijs', 'bekend'),
						{ context, dataType: 'percentage' },
						undefined,
						[new BasicFilterExpression(['ds_nm_tussentijdse_uitstroom_vso'], TussentijdseUitstroomVSO.REGULIER_ONDERWIJS)]
					),
					this.createLeerlingColumn('Regulier', att('regulierOnderwijs'), { context }, undefined, [
						new BasicFilterExpression(['ds_nm_tussentijdse_uitstroom_vso'], TussentijdseUitstroomVSO.REGULIER_ONDERWIJS),
					])
				);
				break;
			case TussentijdseUitstroomVSO.SPECIAAL_ONDERWIJS:
				columns.push(
					this.createLeerlingColumn(
						'% speciaal',
						percOfRow('speciaalOnderwijs', 'bekend'),
						{ context, dataType: 'percentage' },
						undefined,
						[new BasicFilterExpression(['ds_nm_tussentijdse_uitstroom_vso'], TussentijdseUitstroomVSO.SPECIAAL_ONDERWIJS)]
					),
					this.createLeerlingColumn('Speciaal', att('speciaalOnderwijs'), { context }, undefined, [
						new BasicFilterExpression(['ds_nm_tussentijdse_uitstroom_vso'], TussentijdseUitstroomVSO.SPECIAAL_ONDERWIJS),
					])
				);
				break;
		}

		return [
			...columns,
			this.createLeerlingColumn('Leerlingen', att('bekend'), { context }, undefined, [
				new BasicFilterExpression(['ds_nm_tussentijdse_uitstroom_vso'], null, '<>'),
			]),
			createMeasureColumn('Onbekend', att('onbekend'), { context, clickHandler: (path) => this.handleUitzonderingRedirect(path, context) }),
		];
	}

	protected handleUitzonderingRedirect(
		path: Path<TussentijdseUitstroomA, number[]>,
		context: DashboardContext<TussentijdseUitstroomI, TussentijdseUitstroomA, TussentijdseUitstroomComponent>
	) {
		const params = {
			from: this.urlService.getFrom(),
		};

		for (const levelFilter of getLevelFilters(context, path)) {
			Object.assign(params, this.filterService.encodeFilterValue(<FilterName>levelFilter.attr.join('.'), [levelFilter.val]));
		}

		this.urlService.redirectDoorklik([this.getUitzonderingUrl()], params);
	}

	getUitzonderingUrl(): string {
		return '/details/uitzondering/tussentijdse-uitstroom';
	}

	partitionMeasure = computed<PartitionMeasure<TussentijdseUitstroomA>>(() => {
		let attribute: keyof TussentijdseUitstroomA;
		switch (this.tussentijdseUitstroomVso()) {
			case TussentijdseUitstroomVSO.REGULIER_ONDERWIJS:
				attribute = 'regulierOnderwijs';
				break;
			case TussentijdseUitstroomVSO.SPECIAAL_ONDERWIJS:
				attribute = 'speciaalOnderwijs';
				break;
		}
		return {
			type: 'percentage',
			getValue: percOfRow(attribute, 'bekend'),
		};
	});

	protected readonly DashboardVariant = DashboardVariant;

	override enrichTableModel(
		_context: DashboardContext<TussentijdseUitstroomI, TussentijdseUitstroomA, TussentijdseUitstroomComponent>,
		tableModel: TableModel<DataRow<TussentijdseUitstroomA>>
	) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	protected readonly TussentijdseUitstroomVSO = TussentijdseUitstroomVSO;

	override getDisplayOptions(): ExportFilter[] {
		return this.variant() === DashboardVariant.HISTORIE ? [{ label: 'Weergave', value: this.tussentijdseUitstroomVso() }] : [];
	}
}
